/**
 * The "Call notes" message (metadata.call_notes, TRUS-668 v2) as a card:
 * "Call notes · Video call · 3m 12s", the AI summary, action items, decisions,
 * and "View transcript" (speaker-labelled lines). Everything comes from
 * GET /calls/{id}/notes; the title's mode / duration from GET /calls/{id}.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, FileText, Loader2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import {
  formatCallDuration,
  formatTranscriptTime,
  isNotesPending,
  type CallNotesMessageMeta,
} from '@/services/callsApi';
import { callNotesReasonText, callNotesRecordingGoneText } from './callNotesReason';
import { CallNotesRetryButton } from './CallNotesRetryButton';
import { useCallsApi, useLiveCall } from './useCalls';

interface CallNotesCardProps {
  meta: CallNotesMessageMeta;
  /** Blue (own staff) bubble: light text. */
  tone?: 'default' | 'inverted';
  /**
   * v5: false where the surrounding row already names the call (View notes on
   * the call card, the Calls & notes panel) — no title, no GET /calls/{id}.
   */
  showTitle?: boolean;
}

export function CallNotesCard({ meta, tone = 'default', showTitle = true }: CallNotesCardProps) {
  const { t } = useAppTranslation();
  const api = useCallsApi();
  const [showTranscript, setShowTranscript] = useState(false);
  const inverted = tone === 'inverted';
  const live = useLiveCall(meta.call_id, showTitle);
  const notes = useQuery({
    queryKey: ['call-notes', api.kind, meta.call_id],
    queryFn: () => api.getNotes(meta.call_id),
    staleTime: 60_000,
    retry: false,
    refetchInterval: (query) => (isNotesPending(query.state.data?.status) ? 15_000 : false),
  });

  const mode = live.data?.mode;
  const duration = live.data?.duration_seconds;
  const titleParts = [t('messaging.calls.notesTitle', 'Call notes')];
  if (mode) {
    titleParts.push(mode === 'video' ? t('messaging.calls.videoCall', 'Video call') : t('messaging.calls.audioCall', 'Audio call'));
  }
  if (duration != null && duration > 0) titleParts.push(formatCallDuration(duration));

  const muted = inverted ? 'text-white/80' : 'text-muted-foreground';
  const data = notes.data;
  const failed = notes.isError || data?.status === 'failed' || data?.status === 'none';
  // TRUS-708: pending while the worker waits for its keys - say so.
  const waiting = !notes.isError && data?.notes_reason === 'waiting_for_configuration';
  const pending = !failed && !waiting && (notes.isLoading || isNotesPending(data?.status));

  return (
    <div className="flex max-w-xl flex-col gap-2 py-1" data-testid={`call-notes-card-${meta.call_id}`}>
      {showTitle && (
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
            inverted ? 'bg-white/20 text-white' : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
          )}
        >
          <FileText className="h-4 w-4" />
        </div>
        <div className={cn('text-sm font-semibold', inverted && 'text-white')} data-testid="call-notes-title">
          {titleParts.join(' · ')}
        </div>
      </div>
      )}

      {pending ? (
        <div className={cn('flex items-center gap-1 text-xs', muted)} data-testid="call-notes-pending">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('messaging.calls.notesPreparing', 'Notes are being prepared…')}
        </div>
      ) : waiting ? (
        <div className={cn('flex flex-col gap-1 text-xs', muted)} data-testid="call-notes-waiting">
          {callNotesReasonText(t, 'waiting_for_configuration')?.label}
          {data?.notes_retryable && <CallNotesRetryButton callId={meta.call_id} tone={tone} />}
        </div>
      ) : failed || !data ? (
        <div className={cn('flex flex-col gap-1 text-xs', muted)} data-testid="call-notes-failed">
          {data?.status === 'failed' && data.error
            ? t('messaging.calls.notesFailedReason', 'Notes failed: {{reason}}', { reason: data.error })
            : t('messaging.calls.notesUnavailable', 'Notes unavailable')}
          {data?.notes_media_deleted && <span className="italic">{callNotesRecordingGoneText(t)}</span>}
          {data?.notes_retryable && <CallNotesRetryButton callId={meta.call_id} tone={tone} />}
        </div>
      ) : (
        <>
          {data.summary ? (
            <p className={cn('whitespace-pre-wrap text-sm', inverted && 'text-white')} data-testid="call-notes-summary">
              {data.summary}
            </p>
          ) : (
            // Ready with no summary: the transcript was saved but the AI summary failed.
            <p className={cn('text-xs italic', muted)} data-testid="call-notes-no-summary">
              {t('messaging.calls.summaryFailed', "The summary couldn't be generated. The transcript is below.")}
            </p>
          )}
          {data.action_items.length > 0 && (
            <div data-testid="call-notes-actions">
              <div className={cn('text-xs font-semibold uppercase tracking-wide', muted)}>
                {t('messaging.calls.actionItems', 'Action items')}
              </div>
              <ul className={cn('ml-4 list-disc text-sm', inverted && 'text-white')}>
                {data.action_items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {data.decisions.length > 0 && (
            <div data-testid="call-notes-decisions">
              <div className={cn('text-xs font-semibold uppercase tracking-wide', muted)}>
                {t('messaging.calls.decisions', 'Decisions')}
              </div>
              <ul className={cn('ml-4 list-disc text-sm', inverted && 'text-white')}>
                {data.decisions.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {data.transcript.length > 0 && (
            <div>
              <button
                type="button"
                className={cn('flex items-center gap-1 text-xs font-medium underline-offset-2 hover:underline', inverted ? 'text-white' : 'text-primary')}
                onClick={() => setShowTranscript((v) => !v)}
                aria-expanded={showTranscript}
                data-testid="call-notes-transcript-toggle"
              >
                {showTranscript ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {showTranscript
                  ? t('messaging.calls.hideTranscript', 'Hide transcript')
                  : t('messaging.calls.viewTranscript', 'View transcript')}
              </button>
              {showTranscript && (
                <ol
                  className={cn(
                    'mt-1 max-h-72 space-y-1 overflow-y-auto rounded-md p-2 text-sm',
                    inverted ? 'bg-white/10 text-white' : 'bg-muted/60',
                  )}
                  data-testid="call-notes-transcript"
                >
                  {data.transcript.map((line, i) => (
                    <li key={i} className="leading-snug">
                      <span className={cn('mr-1 text-xs tabular-nums', muted)}>{formatTranscriptTime(line.start_ms)}</span>
                      <span className="font-semibold">{line.speaker}</span>
                      <span aria-hidden="true">: </span>
                      <span>{line.text}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CallNotesCard;
